import React, { useState, useEffect } from 'react';

interface DeleteTreeModalProps {
  isOpen: boolean;
  treeId: string;
  treeName: string;
  onClose: () => void;
  onDelete: (treeId: string) => Promise<void>;
}

export const DeleteTreeModal: React.FC<DeleteTreeModalProps> = ({
  isOpen,
  treeId,
  treeName,
  onClose,
  onDelete
}) => {
  const [confirmationId, setConfirmationId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationId('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (confirmationId !== treeId) {
      setError('Tree ID does not match');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onDelete(treeId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tree');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isSubmitEnabled = confirmationId === treeId && !isSubmitting;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Delete Tree</h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        <div className="modal-body">
          <p>Are you sure you want to delete &quot;{treeName}&quot;?</p>
          <p className="delete-warning">This action cannot be undone.</p>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="treeIdDisplay">Tree ID:</label>
              <input
                type="text"
                id="treeIdDisplay"
                value={treeId}
                readOnly
                disabled
                className="form-input"
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmationId">
                Type the tree ID to confirm deletion:
              </label>
              <input
                type="text"
                id="confirmationId"
                value={confirmationId}
                onChange={(e) => {
                  setConfirmationId(e.target.value);
                  setError(null);
                }}
                placeholder={treeId}
                required
                disabled={isSubmitting}
                className={`form-input ${error ? 'form-input-error' : ''}`}
                autoFocus
              />
              {error && (
                <div className="form-error">{error}</div>
              )}
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-danger"
                disabled={!isSubmitEnabled}
              >
                {isSubmitting ? 'Deleting ...' : 'Delete'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

